'use client';

import React from 'react';
import type { QuestionConfig, AnswerData, ValidationRules } from '../types';
import { useTranslation } from '@/app/i18n/client';

interface RadioQuestionProps {
  config: QuestionConfig;
  value: AnswerData;
  onChange: (value: AnswerData) => void;
  validationRules?: ValidationRules;
  error?: string;
  onEnterPress?: () => void; // Callback when Enter is pressed after selection
}

export default function RadioQuestion({
  config,
  value,
  onChange,
  validationRules,
  error,
  onEnterPress,
}: RadioQuestionProps) {
  const { lang } = useTranslation();

  // Helper function to get text in correct language
  const getText = (th: string | undefined, en: string | undefined): string => {
    if (lang === 'en') {
      return en || th || '';
    }
    return th || en || '';
  };

  const selectedValue = value?.value || '';
  const optionsCount = config.options?.length || 0;
  const hasOddOptions = optionsCount % 2 !== 0;
  const layout = config.layout ?? 'horizontal'; // Default to horizontal
  const shouldSpanFull = (index: number, label: string) => hasOddOptions && index === optionsCount - 1 || label.length > 30;

  const handleChange = (optionValue: string) => {
    onChange({ value: optionValue });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && selectedValue && onEnterPress) {
      e.preventDefault();
      onEnterPress();
    }
  };

  // Vertical layout - one pill per row
  if (layout === 'vertical') {
    return (
      <div className="flex flex-col items-center gap-2.5 max-w-xl mx-auto w-full" onKeyDown={handleKeyDown} tabIndex={0}>
        {config.options?.map((option) => (
          <label
            key={option.value}
            className={`flex w-full px-8 py-3 items-center gap-2.5 rounded-[100px] border bg-white cursor-pointer transition-all duration-200 ${
              selectedValue === option.value
                ? 'border-[#028D7B]'
                : 'border-[#DDD] hover:border-[#028D7B]/50'
            } ${error ? 'border-red-300' : ''}`}
          >
            <input
              type="radio"
              name={config.placeholder || 'radio-group'}
              value={option.value}
              checked={selectedValue === option.value}
              onChange={() => handleChange(option.value)}
              className="w-4 h-4 border-gray-300 focus:ring-none accent-[#2A807B]"
            />
            <span className="text-sm text-gray-700">{getText(option.label, option.labelEn)}</span>
          </label>
        ))}
        {error && <p className="text-red-600 text-sm mt-2 text-center">{error}</p>}
      </div>
    );
  }

  // Horizontal layout - existing behavior
  return (
    <div className="flex flex-col items-center gap-2.5 max-w-4xl mx-auto" onKeyDown={handleKeyDown} tabIndex={0}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        {config.options?.map((option, index) => (
          <label
            key={option.value}
            className={`flex w-[260px] px-8 py-3 items-center gap-2.5 rounded-[100px] border bg-white cursor-pointer transition-all duration-200 ${
              selectedValue === option.value
                ? 'border-[#028D7B]'
                : 'border-[#DDD] hover:border-[#028D7B]/50'
            } ${error ? 'border-red-300' : ''}`}
          >
            <input
              type="radio"
              name={config.placeholder || 'radio-group'}
              value={option.value}
              checked={selectedValue === option.value}
              onChange={() => handleChange(option.value)}
              className="w-4 h-4 border-gray-300 focus:ring-none accent-[#2A807B]"
            />
            <span className="text-sm text-gray-700">{getText(option.label, option.labelEn)}</span>
          </label>
        ))}
      </div>
      {error && <p className="text-red-600 text-sm mt-2 text-center">{error}</p>}
    </div>
  );
}
